import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';

const LOGICALUNIT_URL = 'U3207';

const LogicalUnitEndpoints = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SUMMARY: `${LOGICALUNIT_URL}/dashboard/summary`,
  PROVIDER: (providerId: string) => `${LOGICALUNIT_URL}/dashboard/provider/${providerId}`,
  GROUP: (groupId: string) => `${LOGICALUNIT_URL}/dashboard/group/${groupId}`,
  USER: (userId: string) => `${LOGICALUNIT_URL}/dashboard/user/${userId}`,
  CONVERSATIONS: `${LOGICALUNIT_URL}/dashboard/conversations`,
} as const;

export type U3207TileFamily = 'provider' | 'group' | 'conversations' | 'user';

export interface U3207ProviderRef {
  id: string;
  code: string;
  name: string;
}

export interface U3207GroupRef {
  id: string;
  code: string;
  name: string;
  active: boolean;
}

export interface U3207InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  defaultMonth: string;
  tileFamilies: U3207TileFamily[];
  providers: U3207ProviderRef[];
  groups: U3207GroupRef[];
}

export interface U3207Period {
  start: string;
  end: string;
  timezone: string;
  month: string;
}

export interface U3207DashboardQuery {
  month?: string;
  providerId?: string;
  groupId?: string;
  userId?: string;
  level?: 'users';
}

export interface U3207ProviderTile {
  providerId: string;
  code: string;
  name: string;
  tokens: number;
  distinctUsers: number;
  distinctConversations: number;
  orgQuotaMonthlyTokens: number | null;
  orgUsagePercent: number | null;
}

export interface U3207GroupProviderQuota {
  providerId: string;
  code: string;
  name: string;
  tokens: number;
  groupQuotaMonthlyTokens: number | null;
  usagePercent: number | null;
}

export interface U3207GroupTile {
  groupId: string;
  code: string;
  name: string;
  providerCount: number;
  conversationCount: number;
  providers: U3207GroupProviderQuota[];
}

export interface U3207ConversationsTile {
  activeUsers: number;
  conversationCount: number;
}

export interface U3207UserProviderTokens {
  providerId: string;
  code: string;
  name: string;
  tokens: number;
  userQuotaMonthlyTokens: number | null;
  remainingTokens: number | null;
  usagePercent: number | null;
}

export interface U3207UserTile {
  userId: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  conversationCount: number;
  totalTokens: number;
  groupId: string | null;
  groupCode: string | null;
  providers: U3207UserProviderTokens[];
}

export interface U3207DashboardSummary {
  period: U3207Period;
  providers: U3207ProviderTile[];
  groups: U3207GroupTile[];
  conversations: U3207ConversationsTile;
  users: U3207UserTile[];
}

export interface U3207ProviderDrilldown {
  period: U3207Period;
  providerId: string;
  code: string;
  name: string;
  tokens: number;
  distinctUsers: number;
  distinctConversations: number;
  groups: Array<{
    groupId: string;
    code: string;
    name: string;
    tokens: number;
    distinctUsers: number;
    distinctConversations: number;
    groupQuotaMonthlyTokens: number | null;
    usagePercent: number | null;
  }>;
}

export interface U3207GroupDrilldown {
  period: U3207Period;
  groupId: string;
  code: string;
  name: string;
  providers: U3207GroupProviderQuota[];
  users?: Array<{
    userId: string;
    username: string;
    firstName: string | null;
    lastName: string | null;
    tokens: number;
    conversationCount: number;
  }>;
}

export interface U3207ConversationRow {
  conversationId: string;
  title: string | null;
  tokens: number;
  providerId: string | null;
  providerCode: string | null;
  providerName: string | null;
  createdAt: string;
}

export interface U3207UserDrilldown {
  period: U3207Period;
  userId: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  groupId: string | null;
  groupCode: string | null;
  providers: U3207UserProviderTokens[];
  conversations: U3207ConversationRow[];
}

export interface U3207ConversationsDrilldown {
  period: U3207Period;
  users?: Array<{
    userId: string;
    username: string;
    firstName: string | null;
    lastName: string | null;
    conversationCount: number;
    totalTokens: number;
  }>;
  conversations?: U3207ConversationRow[];
}

function buildQueryParams(query?: U3207DashboardQuery): Record<string, string> | undefined {
  if (!query) return undefined;
  const params: Record<string, string> = {};
  if (query.month) params.month = query.month;
  if (query.providerId) params.providerId = query.providerId;
  if (query.groupId) params.groupId = query.groupId;
  if (query.userId) params.userId = query.userId;
  if (query.level) params.level = query.level;
  return Object.keys(params).length ? params : undefined;
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doGetDashboardSummary = (axios: AxiosInstance, query?: U3207DashboardQuery) => {
  return axios.get(LogicalUnitEndpoints.SUMMARY, { params: buildQueryParams(query) });
};

export const doGetProviderDrilldown = (
  axios: AxiosInstance,
  providerId: string,
  query?: U3207DashboardQuery,
) => {
  return axios.get(LogicalUnitEndpoints.PROVIDER(providerId), { params: buildQueryParams(query) });
};

export const doGetGroupDrilldown = (
  axios: AxiosInstance,
  groupId: string,
  query?: U3207DashboardQuery,
) => {
  return axios.get(LogicalUnitEndpoints.GROUP(groupId), { params: buildQueryParams(query) });
};

export const doGetUserDrilldown = (
  axios: AxiosInstance,
  userId: string,
  query?: U3207DashboardQuery,
) => {
  return axios.get(LogicalUnitEndpoints.USER(userId), { params: buildQueryParams(query) });
};

export const doGetConversationsDrilldown = (axios: AxiosInstance, query?: U3207DashboardQuery) => {
  return axios.get(LogicalUnitEndpoints.CONVERSATIONS, { params: buildQueryParams(query) });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string };
  if (apiErr?.errorCode === 'BU320701') {
    errorResult.genericErrors = [
      {
        title: t('errorTitle', { ns: nameSpace }),
        text: t('errInvalidMonth', { ns: nameSpace }),
      },
    ];
  }
  return errorResult;
}
